import { Category, getCategories } from '@/api/category-api';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCollectionData } from '@/hooks/use-api';
import { useDebounce } from '@/hooks/use-debounce';
import { useProductContext } from '@/pages/product/context/product-context';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function ProductFilters() {
  const {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    stockStatus,
    setStockStatus,
    storefrontStatus,
    setStorefrontStatus,
    categoryFilter,
    setCategoryFilter,
  } = useProductContext();

  const { data: categoriesData } = useCollectionData<Category[]>(
    '/categories',
    getCategories
  );
  const categories = categoriesData ?? [];

  // Local state for input to prevent unfocus
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  // Debounce the local search query before updating context
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);

  // Update context when debounced value changes
  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
  }, [debouncedSearchQuery, setSearchQuery]);

  // Sync local state when context searchQuery changes externally (e.g., reset)
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mb-3 w-full max-w-full overflow-x-hidden">
      {/* Search Input */}
      <div className="relative flex-1 sm:max-w-xs min-w-0">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />

        <Input
          ref={inputRef}
          role="searchbox"
          aria-label="Search products"
          placeholder="Search products..."
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setLocalSearchQuery('')}
          className="pl-8 pr-8 h-9 text-sm"
        />

        {localSearchQuery && (
          <button
            type="button"
            onClick={() => setLocalSearchQuery('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Filter Dropdowns */}
      <div className="flex flex-wrap gap-2 shrink-0">
        {/* Category Dropdown */}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[170px] h-9 text-sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="uncategorized">Uncategorized</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={String(cat.id)}>
                {cat.icon ? `${cat.icon} ` : ''}
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Active/Inactive Dropdown */}
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="w-[140px] h-9 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>

        {/* Stock Status Dropdown */}
        <Select value={stockStatus} onValueChange={setStockStatus}>
          <SelectTrigger className="w-[160px] h-9 text-sm">
            <SelectValue placeholder="Stock Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stock</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="out_of_stock">Out of Stock</SelectItem>
          </SelectContent>
        </Select>

        {/* Telegram Mini App Storefront Dropdown */}
        <Select value={storefrontStatus} onValueChange={setStorefrontStatus}>
          <SelectTrigger className="w-[180px] h-9 text-sm">
            <SelectValue placeholder="Telegram Mini App" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Channels</SelectItem>
            <SelectItem value="on_storefront">On Telegram Mini App</SelectItem>
            <SelectItem value="not_on_storefront">Not on Telegram</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
